import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Crown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { VideoEmbed } from "../components/VideoEmbed";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { ISSUE_LABELS } from "../lib/constants";

export default function Gallery() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [tags, setTags] = useState(["general"]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ title: "", embed_url: "", issue_tag: "general", description: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/transformations").then((r) => { setItems(r.data.transformations); setTags(r.data.issue_tags); }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.embed_url.trim()) { toast.error("Add a title and video link."); return; }
    setSaving(true);
    try {
      await api.post("/transformations", form);
      toast.success("Transformation added.");
      setForm({ title: "", embed_url: "", issue_tag: "general", description: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/transformations/${id}`); toast.success("Removed."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const shown = items?.filter((it) => filter === "all" || it.issue_tag === filter) || [];

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-12">
        <span className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold"><Crown className="h-4 w-4" /> Real Transformations</span>
        <h1 className="mt-2 font-heading text-4xl text-cream md:text-5xl">See the work, up close</h1>
        <p className="mt-2 max-w-lg text-muted-foreground">Before &amp; after videos of real scalp and loc treatments, tagged by the issue they resolve.</p>

        {isAdmin && (
          <div className="mt-8 rounded-2xl border border-gold/40 bg-charcoal-surface p-6 md:p-8" data-testid="admin-add-transformation">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl text-cream"><Plus className="h-5 w-5 text-gold" /> Add a transformation (admin)</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="text-cream">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Buildup removal — mature locs" className="mt-1 border-charcoal-border bg-charcoal text-cream" data-testid="transform-title-input" />
              </div>
              <div>
                <Label className="text-cream">Video link (YouTube / Vimeo / Instagram)</Label>
                <Input value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })} placeholder="https://youtu.be/…" className="mt-1 border-charcoal-border bg-charcoal text-cream" data-testid="transform-url-input" />
              </div>
              <div>
                <Label className="text-cream">Issue tag</Label>
                <Select value={form.issue_tag} onValueChange={(v) => setForm({ ...form, issue_tag: v })}>
                  <SelectTrigger className="mt-1 border-charcoal-border bg-charcoal text-cream" data-testid="transform-tag-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {tags.map((t) => <SelectItem key={t} value={t}>{ISSUE_LABELS[t] || t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-cream">Short description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" className="mt-1 border-charcoal-border bg-charcoal text-cream" data-testid="transform-desc-input" />
              </div>
            </div>
            <Button onClick={add} disabled={saving} className="mt-4 rounded-full bg-gold text-charcoal hover:bg-gold/90" data-testid="transform-add-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add transformation"}
            </Button>
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-2">
          {["all", ...tags].map((t) => (
            <button key={t} onClick={() => setFilter(t)}
              className={`rounded-full border px-4 py-1.5 text-xs transition-colors ${filter === t ? "border-gold bg-gold/10 text-gold" : "border-charcoal-border text-muted-foreground hover:text-cream"}`}
              data-testid={`gallery-filter-${t}`}>
              {t === "all" ? "All" : (ISSUE_LABELS[t] || t)}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {items === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
          ) : shown.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-charcoal-border bg-charcoal-surface py-20 text-center" data-testid="gallery-empty">
              <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-gold/30 text-gold"><Crown className="h-6 w-6" strokeWidth={1.4} /></span>
              <h3 className="font-heading text-3xl text-cream">Coming soon</h3>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                {isAdmin ? "Add your first transformation using the form above." : "Real before & after treatment videos are on their way. Watch this space."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
              {shown.map((it, i) => (
                <motion.div key={it.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="overflow-hidden rounded-xl border border-charcoal-border bg-charcoal-surface" data-testid={`transformation-${it.id}`}>
                  <VideoEmbed url={it.embed_url} title={it.title} />
                  <div className="p-5">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{ISSUE_LABELS[it.issue_tag] || it.issue_tag}</span>
                    <h3 className="mt-1 font-heading text-xl leading-tight text-cream">{it.title}</h3>
                    {it.description && <p className="mt-1 text-sm text-muted-foreground">{it.description}</p>}
                    {isAdmin && (
                      <button onClick={() => remove(it.id)} className="mt-3 flex items-center gap-1 text-xs text-destructive hover:underline" data-testid={`transform-delete-${it.id}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
